import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { setChainTestListAssignments } from "../../../services/diagnosticLabsService";
import { Drawer, PrimaryButton, SecondaryButton, plural } from "./ui";
import LabChecklist from "./LabChecklist";
import ConfirmChangesDialog from "./ConfirmChangesDialog";
import { computeAssignmentDiff, selectionFromList } from "./assignment";

// Choose which member labs use a list. The selection is the complete set; the
// backend re-validates chain membership and saves it synchronously.
const AssignLabsDrawer = ({ open, onClose, role, chainId, list, labs, onSaved }) => {
  const [selected, setSelected] = useState(() => selectionFromList(list));
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset only when the drawer opens (or switches list) — a background refresh
  // of the same list must not wipe the user's in-progress selection.
  useEffect(() => {
    if (open) {
      setSelected(selectionFromList(list));
      setError("");
      setConfirming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, list?.id]);

  const diff = useMemo(() => computeAssignmentDiff(labs, list, selected), [labs, list, selected]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      // The user reviewed every move in the dialog, so moves are explicit here.
      await setChainTestListAssignments(role, chainId, list.id, [...selected], diff.moved.length > 0);
      setConfirming(false);
      onSaved?.(`${list.name} now serves ${plural(selected.size, "lab")}.`);
      onClose();
    } catch (err) {
      setConfirming(false);
      setError(
        err.code === "reassign_required"
          ? "Another user changed these assignments meanwhile. The list has been refreshed — review and try again."
          : err.message || "Couldn't save assignments."
      );
      if (err.code === "reassign_required") onSaved?.(null);
    } finally {
      setSaving(false);
    }
  };

  const summary = diff.changed
    ? [diff.added.length && `+${diff.added.length} added`, diff.moved.length && `${diff.moved.length} moved`, diff.removed.length && `−${diff.removed.length} removed`]
        .filter(Boolean) // ok: joins summary fragments, not a contract field
        .join(" · ")
    : "No changes";

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        busy={saving}
        title={list ? `Labs using “${list.name}”` : "Assign labs"}
        subtitle={list ? `${plural(list.test_count, "test")} · labs you tick will expose this list` : ""}
        footer={(
          <div className="flex items-center justify-between gap-3">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span key={summary} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className={`text-xs font-medium ${diff.changed ? "text-[#0F4C81]" : "text-gray-400"}`}>
                {summary}
              </motion.span>
            </AnimatePresence>
            <div className="flex gap-2">
              <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
              <PrimaryButton onClick={() => setConfirming(true)} disabled={!diff.changed || saving}>Review changes</PrimaryButton>
            </div>
          </div>
        )}
      >
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <p className="text-xs text-gray-500">
            A lab can use one chain list at a time. Unticked labs use their own direct test upload.
          </p>
          {list && <LabChecklist labs={labs} selected={selected} onChange={setSelected} listId={list.id} disabled={saving} />}
        </div>
      </Drawer>
      <ConfirmChangesDialog
        open={confirming}
        list={list}
        diff={diff}
        busy={saving}
        onCancel={() => setConfirming(false)}
        onConfirm={save}
      />
    </>
  );
};

export default AssignLabsDrawer;
